/**
 * Оплата СБП или картой через Lava: счёт → страница оплаты → ожидание.
 *
 * Страница Lava открывается во внешнем браузере, и приложение не узнаёт,
 * чем она закончилась. Поэтому после открытия платёж опрашивается, пока
 * сервер не получит вебхук и не зачислит срок. Опрос длится до 15 минут —
 * столько обычно хватает, чтобы подтвердить оплату в банковском приложении.
 * Человек может прекратить ожидание: если оплата всё же пройдёт, срок
 * зачислится, а бот пришлёт сообщение.
 */
import { hapticFeedback } from '@telegram-apps/sdk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { apiGet, apiPost } from '@/shared/api/client';
import { lavaInvoiceSchema, paymentStatusSchema } from '@/shared/api/schemas';
import { openExternal } from '@/shared/lib/links';

import { messageOf, type PayRequest, type PayResult } from './usePayStars';

export type LavaMethod = 'sbp' | 'card';
export type LavaPhase = 'idle' | 'creating' | 'waiting';

const POLL_INTERVAL_MS = 3000;
const WAIT_LIMIT_MS = 15 * 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function usePayLava() {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<LavaPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayResult | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);
  // Ожидание прерывается флагом, а не отменой промиса: опрос просто
  // перестаёт спрашивать на следующем шаге.
  const cancelled = useRef(false);
  // Флаг «оплата уже идёт» — в ref, а не в state: два быстрых нажатия
  // успевают раньше перерисовки и иначе выставили бы два счёта.
  const busy = useRef(false);

  // Ушли с экрана — ожидание прекращается: иначе опрос шёл бы ещё 15 минут
  // в фоне, а его результат уже некому показать.
  useEffect(
    () => () => {
      cancelled.current = true;
    },
    [],
  );

  /** Возвращает true, когда сервер подтвердил оплату. */
  const pay = async (request: PayRequest, method: LavaMethod): Promise<boolean> => {
    if (busy.current) return false;
    busy.current = true;
    setError(null);
    cancelled.current = false;

    setPhase('creating');
    try {
      const created = await apiPost('/payments/lava', { ...request, method }, lavaInvoiceSchema);
      setPayUrl(created.pay_url);
      openExternal(created.pay_url);

      setPhase('waiting');
      const deadline = Date.now() + WAIT_LIMIT_MS;
      while (!cancelled.current && Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        try {
          const payment = await apiGet(`/payments/${created.payment_id}`, paymentStatusSchema);
          if (payment.status === 'succeeded') {
            await queryClient.invalidateQueries({ queryKey: ['subscription'] });
            if (hapticFeedback.notificationOccurred.isAvailable()) {
              hapticFeedback.notificationOccurred('success');
            }
            setResult({ payment });
            return true;
          }
        } catch {
          // Сеть мигнула — продолжаем ждать: оплата могла уже пройти.
        }
      }
      if (!cancelled.current) {
        setError('Оплата пока не подтверждена. Если вы оплатили, срок зачислится автоматически.');
      }
      return false;
    } catch (caught) {
      setError(messageOf(caught));
      return false;
    } finally {
      busy.current = false;
      setPhase('idle');
    }
  };

  return {
    pay,
    phase,
    error,
    result,
    /** Снова открыть страницу оплаты, если человек случайно её закрыл. */
    reopen: () => payUrl && openExternal(payUrl),
    cancel: () => {
      cancelled.current = true;
    },
    reset: () => {
      setResult(null);
      setError(null);
    },
  };
}
